import React, { useState, useEffect } from 'react';
import { Check, X, Zap, Loader2, ArrowRight, Crown, Infinity } from 'lucide-react';
import { subscriptionService } from '../services/api/subscriptionService';
import { useToastContext } from '../contexts/ToastContext';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEATURES = [
  { t: 'Verified Badge', d: 'Stand out to coaches with the blue check.' },
  { t: 'Magic Messenger', d: 'Unlimited automated social outreach.' },
  { t: 'Video Analysis', d: 'Upload film for verified metric badges.' },
  { t: 'Priority Inbox', d: 'Your messages land at the top of coach boards.' },
];

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  const { error: showError } = useToastContext();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'lifetime'>('lifetime');
  const [loading, setLoading] = useState(false);
  const [promoPlan, setPromoPlan] = useState<'monthly' | 'lifetime' | 'founding' | null>(null);

  useEffect(() => {
    const promo = localStorage.getItem('scoutloop_promo');
    if (promo === 'founding') {
      setPromoPlan('founding');
      setSelectedPlan('lifetime'); // show lifetime tab as selected visually
    }
  }, []);

  if (!isOpen) return null;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      // Founding rate only applies to the Lifetime tier — if the athlete switches to
      // Monthly, charge the normal monthly subscription with no coupon applied.
      const couponId =
        promoPlan === 'founding' && selectedPlan === 'lifetime'
          ? subscriptionService.foundingCoupon
          : undefined;
      const url = await subscriptionService.createCheckoutSession(selectedPlan, couponId);
      // Clear the promo flag once checkout starts
      localStorage.removeItem('scoutloop_promo');
      window.location.href = url;
    } catch (err) {
      showError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-xl z-[300] flex items-center justify-center p-4">
      <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row max-h-[95vh] border border-white/20 animate-in zoom-in-95 duration-200">

        {/* Left: Benefits */}
        <div className="md:w-1/2 bg-slate-900 p-10 text-white flex flex-col justify-between relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <Zap className="text-amber-500" size={32} />
              <h2 className="text-3xl font-black uppercase italic tracking-tighter">Go Elite Pro</h2>
            </div>
            <ul className="space-y-6">
              {FEATURES.map((item) => (
                <li key={item.t} className="flex gap-4">
                  <div className="bg-amber-600/20 p-1.5 rounded-lg h-fit text-amber-400">
                    <Check size={18} />
                  </div>
                  <div>
                    <p className="font-black uppercase italic text-sm leading-none mb-1">{item.t}</p>
                    <p className="text-xs text-slate-400 font-medium">{item.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative z-10 p-6 bg-white/5 border border-white/10 rounded-2xl mt-8">
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-2">
              Team Pricing
            </p>
            <p className="text-xs font-medium text-slate-300">
              Coaches: Want ScoutLoop for your entire team? Contact us for group rates.
            </p>
          </div>

          <Zap size={400} className="absolute -bottom-20 -right-20 text-white/5" />
        </div>

        {/* Right: Plan Selection */}
        <div className="md:w-1/2 bg-white p-10 flex flex-col overflow-y-auto">
          {promoPlan === 'founding' && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-6 flex items-center gap-2">
              <span className="text-amber-600 text-lg">⚡</span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                  Founding Athlete Offer Applied
                </p>
                <p className="text-[10px] text-amber-600 font-medium">
                  Lifetime access for $49 — Independence High School exclusive
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-2xl font-black uppercase italic">Choose a Plan</h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:bg-slate-50 rounded-full transition">
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 space-y-4">
            {/* Lifetime Plan */}
            <button
              onClick={() => setSelectedPlan('lifetime')}
              className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${
                selectedPlan === 'lifetime'
                  ? 'border-amber-600 bg-amber-50'
                  : 'border-slate-100 hover:border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${selectedPlan === 'lifetime' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    <Infinity size={18} />
                  </div>
                  <div>
                    <p className="font-black uppercase italic text-slate-900">Lifetime Access</p>
                    <p className="text-xs text-slate-400 font-medium mt-0.5">Pay once, own it forever.</p>
                  </div>
                </div>
                <div className="text-right">
                  {promoPlan === 'founding' ? (
                    <>
                      <p className="text-2xl font-black italic text-slate-900">$49</p>
                      <p className="text-[10px] font-bold text-amber-600 uppercase">Founding Rate</p>
                      <p className="text-[10px] font-bold text-slate-300 line-through">$79</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-black italic text-slate-900">$79</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">one-time</p>
                    </>
                  )}
                </div>
              </div>
              {selectedPlan === 'lifetime' && (
                <div className="mt-3 pt-3 border-t border-amber-100">
                  <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest">
                    Best Value — Save over $40/year
                  </p>
                </div>
              )}
            </button>

            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${
                selectedPlan === 'monthly'
                  ? 'border-amber-600 bg-amber-50'
                  : 'border-slate-100 hover:border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${selectedPlan === 'monthly' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    <Crown size={18} />
                  </div>
                  <div>
                    <p className="font-black uppercase italic text-slate-900">Monthly Pro</p>
                    <p className="text-xs text-slate-400 font-medium mt-0.5">Cancel anytime from settings.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black italic text-slate-900">$9.99</p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">/month</p>
                </div>
              </div>
            </button>

            <button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full bg-amber-600 text-white py-5 rounded-2xl font-black uppercase italic tracking-widest text-sm flex items-center justify-center gap-3 shadow-2xl hover:bg-amber-700 transition active:scale-95 disabled:opacity-50 mt-4"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <>
                  Continue to Checkout
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-[9px] text-gray-400 font-bold uppercase leading-relaxed">
              Secure checkout via Stripe. No card stored by ScoutLoop.{' '}
              <br />
              By upgrading, you agree to ScoutLoop&apos;s Terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
